import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, MapPin } from "lucide-react";
import api from "@/lib/api";
import { inputCls, Label, GoldButton } from "@/components/Field";
import { usePageTitle } from "@/hooks/use-page-title";

const TOKEN_KEY = "aspira_admin_token";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const EMPTY = {
  title: "", state: "", county: "", acreage: "", price: "", parcel_number: "",
  access: "", features: "", image_url: "", description: "", status: "available",
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin();
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 py-24 sm:px-8" data-testid="admin-login">
      <form onSubmit={submit} className="rounded-3xl border border-aspira-goldborder bg-white p-8 shadow-xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">Aspira admin</p>
        <h1 className="mt-3 font-serif text-3xl font-medium text-aspira-ink">Sign in</h1>
        <div className="mt-7 space-y-5">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <input id="admin-email" data-testid="admin-email" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <input id="admin-password" data-testid="admin-password" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>
        <GoldButton type="submit" data-testid="admin-login-submit" disabled={busy} className="mt-7 w-full">
          {busy ? "Signing in…" : "Sign In"}
        </GoldButton>
      </form>
    </div>
  );
};

const ListingForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const editing = Boolean(initial?.id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/listings/${initial.id}`, form, authHeaders());
        toast.success("Property updated");
      } else {
        await api.post("/admin/listings", form, authHeaders());
        toast.success("Property added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="listing-form" className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-8">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-medium text-aspira-ink">{editing ? "Edit property" : "Add a property"}</h2>
        <button type="button" data-testid="listing-form-cancel" onClick={onCancel} className="rounded-lg p-2 text-aspira-muted hover:text-aspira-ink">
          <X size={18} />
        </button>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="lf-title">Title *</Label>
          <input id="lf-title" data-testid="lf-title" required className={inputCls} placeholder="e.g. 10 Acres in Brewster County" value={form.title} onChange={set("title")} />
        </div>
        <div>
          <Label htmlFor="lf-state">State *</Label>
          <input id="lf-state" data-testid="lf-state" required className={inputCls} value={form.state} onChange={set("state")} />
        </div>
        <div>
          <Label htmlFor="lf-county">County</Label>
          <input id="lf-county" data-testid="lf-county" className={inputCls} value={form.county} onChange={set("county")} />
        </div>
        <div>
          <Label htmlFor="lf-acreage">Acreage</Label>
          <input id="lf-acreage" data-testid="lf-acreage" className={inputCls} placeholder="e.g. 10" value={form.acreage} onChange={set("acreage")} />
        </div>
        <div>
          <Label htmlFor="lf-price">Asking price</Label>
          <input id="lf-price" data-testid="lf-price" className={inputCls} placeholder="e.g. $24,900" value={form.price} onChange={set("price")} />
        </div>
        <div>
          <Label htmlFor="lf-parcel">Parcel #</Label>
          <input id="lf-parcel" data-testid="lf-parcel" className={inputCls} value={form.parcel_number} onChange={set("parcel_number")} />
        </div>
        <div>
          <Label htmlFor="lf-access">Access</Label>
          <input id="lf-access" data-testid="lf-access" className={inputCls} placeholder="e.g. Dirt road frontage" value={form.access} onChange={set("access")} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-features">Features</Label>
          <input id="lf-features" data-testid="lf-features" className={inputCls} placeholder="e.g. Level terrain, mountain views" value={form.features} onChange={set("features")} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-image">Photo URL</Label>
          <input id="lf-image" data-testid="lf-image" className={inputCls} placeholder="https://…" value={form.image_url} onChange={set("image_url")} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-description">Description</Label>
          <textarea id="lf-description" data-testid="lf-description" rows={4} className={inputCls} value={form.description} onChange={set("description")} />
        </div>
        <div>
          <Label htmlFor="lf-status">Status</Label>
          <select id="lf-status" data-testid="lf-status" className={inputCls} value={form.status} onChange={set("status")}>
            <option value="available">Available</option>
            <option value="pending">Pending</option>
            <option value="sold">Sold</option>
          </select>
        </div>
      </div>
      <GoldButton type="submit" data-testid="listing-form-submit" disabled={busy} className="mt-7">
        {busy ? "Saving…" : editing ? "Save Changes" : "Add Property"}
      </GoldButton>
    </form>
  );
};

const Dashboard = ({ onLogout }) => {
  const [listings, setListings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/listings", authHeaders());
      setListings(data.listings);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load properties");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/listings/${id}`, authHeaders());
      toast.success("Property removed");
      load();
    } catch (err) {
      toast.error("Could not remove property");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">Aspira admin</p>
          <h1 className="mt-2 font-serif text-3xl font-medium text-aspira-ink">Land listings</h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-add-listing"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 rounded-full bg-aspira-gold px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-aspira-goldhover"
          >
            <Plus size={15} /> Add Property
          </button>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-aspira-ink/15 px-5 py-2.5 text-sm font-semibold text-aspira-ink transition-colors hover:border-aspira-gold hover:text-aspira-gold"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mt-8">
          <ListingForm
            initial={editing}
            onSaved={() => { setShowForm(false); setEditing(null); load(); }}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      <div className="mt-8 space-y-4">
        {listings === null && <p className="text-sm text-aspira-muted">Loading…</p>}
        {listings?.length === 0 && (
          <div className="rounded-3xl border border-dashed border-aspira-goldborder bg-white/70 p-10 text-center" data-testid="admin-empty">
            <p className="font-serif text-xl text-aspira-ink">No properties yet</p>
            <p className="mt-2 text-sm text-aspira-body">Add your first parcel and it will appear on the Buy Land page instantly.</p>
          </div>
        )}
        {listings?.map((l, i) => (
          <div
            key={l.id}
            data-testid={`admin-listing-${i + 1}`}
            className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-aspira-goldborder/60 bg-white p-5"
          >
            <div className="flex items-center gap-4">
              {l.image_url ? (
                <img src={l.image_url} alt={l.title} className="h-14 w-14 rounded-xl object-cover" />
              ) : (
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-aspira-sand text-aspira-gold">
                  <MapPin size={18} />
                </span>
              )}
              <div>
                <p className="font-serif text-lg font-medium text-aspira-ink">{l.title}</p>
                <p className="text-xs text-aspira-muted">
                  {[l.county, l.state].filter(Boolean).join(", ")} {l.acreage ? `· ${l.acreage} acres` : ""} {l.price ? `· ${l.price}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                l.status === "available" ? "bg-aspira-goldlight text-aspira-gold" : "bg-aspira-sand text-aspira-muted"
              }`}>
                {l.status}
              </span>
              <button
                data-testid={`admin-edit-${i + 1}`}
                onClick={() => { setEditing(l); setShowForm(true); }}
                className="rounded-lg p-2 text-aspira-muted transition-colors hover:text-aspira-gold"
                aria-label="Edit"
              >
                <Pencil size={16} />
              </button>
              <button
                data-testid={`admin-delete-${i + 1}`}
                onClick={() => remove(l.id)}
                className="rounded-lg p-2 text-aspira-muted transition-colors hover:text-red-600"
                aria-label="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Admin = () => {
  usePageTitle("Admin | Aspira Property Group");
  const [authed, setAuthed] = useState(() => Boolean(localStorage.getItem(TOKEN_KEY)));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed
    ? <Dashboard onLogout={logout} />
    : <Login onLogin={() => setAuthed(true)} />;
};

export default Admin;
